import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  TextField,
  InputAdornment,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import baseURL from "../../config";

const ClosedCVsList = () => {
  const [closedCVs, setClosedCVs] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const vesselOwnerId = localStorage.getItem("urlID");
  const isMobile = useMediaQuery("(max-width:768px)");

  useEffect(() => {
    const fetchClosedCVs = async () => {
      try {
        const res = await axios.get(
          `${baseURL}/api/vacancyBasedCrewCVs/vesselowner/${vesselOwnerId}`
        );
        setClosedCVs(res.data.data || []);
      } catch (err) {
        setError(err.response?.data?.message || "Error fetching CVs");
      }
    };

    if (vesselOwnerId) fetchClosedCVs();
  }, [vesselOwnerId]);

  // 🔍 Search filter
  const filteredCVs = closedCVs.filter((cv) => {
    const matchedCv = cv.crewCvId?.cvs?.find((c) => c._id === cv.cvId) || {};
    const name = matchedCv.name?.toLowerCase() || "";
    const rank = matchedCv.rank?.toLowerCase() || "";
    const vacancy = cv.vacancyId?.title?.toLowerCase() || "";

    return (
      name.includes(searchTerm.toLowerCase()) ||
      rank.includes(searchTerm.toLowerCase()) ||
      vacancy.includes(searchTerm.toLowerCase())
    );
  });

  if (error) {
    return (
      <Typography color="error" align="center">
        {error}
      </Typography>
    );
  }

  return (
    <Box p={{ xs: 2, md: 4 }}>
      {/* Title */}
      <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ color: "#C30B12" }}>
        Selected CVs
      </Typography>

      {/* 🔍 Search bar */}
      <TextField
        variant="outlined"
        placeholder="Search by name, rank, or vacancy..."
        fullWidth
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3, backgroundColor: "white", borderRadius: 2 }}
      />

      {filteredCVs.length === 0 ? (
        <Typography>No Selected CVs found.</Typography>
      ) : isMobile ? (
        // 📱 Mobile Card View
        <Stack spacing={2}>
          {filteredCVs.map((cv) => {
            const matchedCv =
              cv.crewCvId?.cvs?.find((c) => c._id === cv.cvId) || {};

            return (
              <Card
                key={cv._id}
                elevation={3}
                sx={{ borderRadius: 3, overflow: "hidden" }}
              >
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {cv.vacancyId?.title || "N/A"}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" color="textSecondary">
                    Crew:{" "}
                    <span style={{ fontWeight: 600 }}>
                      {matchedCv.name || "N/A"}
                    </span>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Rank:{" "}
                    <span style={{ fontWeight: 600 }}>
                      {matchedCv.rank || "N/A"}
                    </span>
                  </Typography>

                  <Box mt={1}>
                    {cv.statusHistory?.map((status, idx) => (
                      <Chip
                        key={idx}
                        label={`${status.statusType} • ${new Date(
                          status.updatedAt
                        ).toLocaleDateString()}`}
                        color={
                          status.statusType.toLowerCase() === "closed"
                            ? "error"
                            : "default"
                        }
                        size="small"
                        sx={{
                          mr: 1,
                          mt: 1,
                          borderRadius: "8px",
                          fontWeight: 500,
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      ) : (
        // 💻 Desktop Table View
        <Paper
          elevation={4}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Box sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell sx={{ fontWeight: "bold", color: "#C30B12" }}>Vacancy</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#C30B12" }}>Crew Name</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#C30B12" }}>Rank</TableCell>
                  <TableCell sx={{ fontWeight: "bold", color: "#C30B12" }}>
                    Status History
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCVs.map((cv) => {
                  const matchedCv =
                    cv.crewCvId?.cvs?.find((c) => c._id === cv.cvId) || {};

                  return (
                    <TableRow
                      key={cv._id}
                      hover
                      sx={{
                        "&:hover": { backgroundColor: "#f9fafb" },
                        transition: "0.2s",
                      }}
                    >
                      <TableCell>{cv.vacancyId?.title || "N/A"}</TableCell>
                      <TableCell>{matchedCv.name || "N/A"}</TableCell>
                      <TableCell>{matchedCv.rank || "N/A"}</TableCell>
                      <TableCell>
                        {cv.statusHistory?.map((status, idx) => (
                          <Chip
                            key={idx}
                            label={`${status.statusType} • ${new Date(
                              status.updatedAt
                            ).toLocaleDateString()}`}
                            color={
                              status.statusType.toLowerCase() === "closed"
                                ? "error"
                                : "default"
                            }
                            size="small"
                            sx={{
                              mr: 1,
                              mb: 1,
                              borderRadius: "8px",
                              fontWeight: 500,
                            }}
                          />
                        ))}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default ClosedCVsList;
